import { ChangeEvent, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import ReactECharts from 'echarts-for-react';

// Constants
import {
  INTERVAL_UPDATE_DATA,
  PAGE_TITLE,
  PAGE_TITLE_COMPONENT,
  TEXT_LENGTH_LIMITS,
} from '../../../../../constants/common';

// Store
import { useAppDispatch, useAppSelector } from '../../../../../store/store';

// Types
import { ElementSize } from '../../../../../types/element-size';
import { ButtonType } from '../../../../../types/button-type';

// Enums
import CycleMeasurement from '../../../../../enums/cycle-measurement';
import { Action } from '../../../../../enums/action';

// Utils
import fahrenheitToCelsius from '../../../../../utils/fahrenheitToCelsius';

// Components
import Modal from '../../../../Modal/Modal';
import InteractiveButton from '../../../../InteractiveButton/InteractiveButton';
import SpinnerLoader from '../../../../SpinnerLoader/SpinnerLoader';
import TextAreaControl from '../../../../TextAreaControl/TextAreaControl';
import SelectOption, { Option, SelectedValue } from '../../../../SelectOption/SelectOption';
import { AbilityContext } from '../../../../Can/Can';
import { CycleComponents, NoteSeriesDataType } from '../../../../CycleChartPreview/types';
import { useTitle } from '../../../../../hooks/useTitle';
import { generateControllersName, generateCycleData, getNumberNoteSeries, sortNoteSeries } from '../../../utils';
import { CycleController } from '../../../../../store/models/controllers';
import getDeviceNamesUtil from '../../../../../utils/getDeviceNames';
import getTempByMeasurement from '../../../../../utils/getTempByMeasurement';
import getMeasurementPreference from '../../../../../utils/getMeasurementPreference';
import { ChartPreference } from '../../../../../store/models/chart-preference';
import applyYAxisPadding from '../../../../../utils/applyYAxisPadding';
import { Note, NoteLink } from '../../../types';
import { NOTE_ID } from '../../../../CycleChartPreview/constants';
import { useTranslation } from 'react-i18next';
import { IoTDataResponse } from '../../../../../store/models/cycles';
import { TIME } from '../../../../../constants/time.constants';
import { ChartPreferencesWrapper } from './modules/ChartPreferences/ChartPreferencesWrapper';
import { ReportChannelsWrapper } from './modules/ReportChannels/ReportChannelsWrapper';
import { LiveChart } from '../../../../CycleChartPreview/LiveChart';
import { CHART_QUERY_PARAM_NAME } from '../../../constants';
import { TimeRange } from './modules/TimeRange';
import { TIME_RANGE_1_HOUR_OPTION } from './modules/TimeRange/constants';

type NoeFormValues = {
  id: string | null;
  name: string | null;
  coordinate: { x: number | null; y: number | null };
};

export const LiveData = () => {
  const defaultBucket = import.meta.env.VITE_APP_CHART_BUCKET ?? '1';
  const [searchParams, setSearchParams] = useSearchParams();
  const { id } = useParams();
  const { t } = useTranslation();
  const { excludedChannelsNames, colorDevices } = useAppSelector((state) => state.cycles);

  const { cycle } = useAppSelector((state) => state.cycles);
  const { contract } = useAppSelector((state) => state.contracts);
  const { currentLocation } = useAppSelector((state) => state.locations);
  const { currentUserMember } = useAppSelector((state) => state.members);
  const { chartPreference } = useAppSelector((state) => state.chart_preference);

  const currentUserMeasurement = useMemo(() => {
    return getMeasurementPreference(contract, currentLocation);
  }, [contract, currentLocation]);

  const { cycleControllers, cycleIotData, cycleNotes } = useMemo(() => {
    return {
      cycleControllers: cycle?.cycle_controllers,
      cycleIotData: cycle?.iotDataResponse,
      cycleNotes: cycle?.notes,
    };
  }, [cycle]);

  const {
    cycles: { getChartCycleData: getCycle, setOneData: updateCycleData },
    note: { createNote, deleteNote, updateNote, createNoteLink, deleteNoteLink },
    members: { updateViewNotes },
    channels: { getChannels },
  } = useAppDispatch();

  const setBucketQuery = (bucket: string) => {
    setSearchParams({
      [CHART_QUERY_PARAM_NAME.BUCKET]: bucket,
    });
  };

  const setupDefaultBucket = () => {
    setBucketQuery(defaultBucket);
  };

  const setActiveBucket = (urLSearchParams: URLSearchParams) => {
    const data = urLSearchParams.get(CHART_QUERY_PARAM_NAME.BUCKET);

    const size = parseInt(data);

    if (isNaN(size) || !size) {
      return setupDefaultBucket();
    }

    setBucketQuery(data);
  };

  useEffect(() => {
    setActiveBucket(searchParams);
  }, [searchParams]);

  const bucket = useMemo(() => {
    return searchParams.get(CHART_QUERY_PARAM_NAME.BUCKET);
  }, [searchParams]);

  const ability = useContext(AbilityContext);
  const IS_ACCESS = {
    NOTE: {
      CREATE: ability.can(Action.Create, PAGE_TITLE_COMPONENT[PAGE_TITLE.CYCLE].NOTE),
      DELETE: ability.can(Action.Delete, PAGE_TITLE_COMPONENT[PAGE_TITLE.CYCLE].NOTE),
      UPDATE: ability.can(Action.Update, PAGE_TITLE_COMPONENT[PAGE_TITLE.CYCLE].NOTE),
    },
    CYCLE: {
      UPDATE: ability.can(Action.Read, PAGE_TITLE.EDIT_CYCLE),
    },
    CHART_PREFERENCES: {
      UPDATE: ability.can(Action.Update, PAGE_TITLE_COMPONENT[PAGE_TITLE.CYCLE].CHART_PREFERENCES),
    },
  };

  const chartRef = useRef<ReactECharts | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [lastFetchedDate, setLastFetchedDate] = useState<number>(0);
  const [dateRange, setDateRange] = useState<number>(0);
  const [hourRange, setHourRange] = useState<number>(TIME_RANGE_1_HOUR_OPTION.value);
  const [minTemp, setMinTemp] = useState<number>(null);
  const [maxTemp, setMaxTemp] = useState<number>(null);
  const [isCreationNote, setIsCreationNote] = useState<boolean>(false);
  const [isEditNote, setIsEditNote] = useState<boolean>(false);
  const [isDeletionNote, setIsDeletionNote] = useState<boolean>(false);
  const [isDeletingChain, setIsDeletingChain] = useState<boolean>(false);
  const [linkTargetNoteId, setLinkTargetNoteId] = useState<string | null>(null);
  const [editingOccurrenceId, setEditingOccurrenceId] = useState<string>('root');
  const [linkingNoteId, setLinkingNoteId] = useState<string | null>(null);
  const linkingNoteIdRef = useRef<string | null>(null);
  const cycleRef = useRef(cycle);
  const isDeletingChainRef = useRef<boolean>(false);

  const [cycleComponents, setCycleComponent] = useState<CycleComponents | null>(null);
  const [iotData, setIotData] = useState(null);
  const [cycleControllersName, setCycleControllersName] = useState<{ [key: string]: string } | null>(null);
  const [cycleName, setCycleName] = useState<string>('undefined');
  const [nameDevices, setNameDevices] = useState<{ [key: string]: string } | null>(null);
  const [tempRange, setTempRange] = useState<Pick<ChartPreference, 'min_temperature' | 'max_temperature'> | null>(null);

  const isStopPullingIotData = isLoading || isCreationNote || isDeletionNote || !!linkingNoteId;

  useEffect(() => {
    linkingNoteIdRef.current = linkingNoteId;
  }, [linkingNoteId]);

  useEffect(() => {
    isDeletingChainRef.current = isDeletingChain;
  }, [isDeletingChain]);

  useEffect(() => {
    cycleRef.current = cycle;
  }, [cycle]);

  useTitle(cycleName);

  const generateNoteName = (name: string) => {
    return `${NOTE_ID}: ${name}`;
  };

  const onSubmitFormHandler = async (values: NoeFormValues) => {
    if (isCreationNote) {
      const { name, coordinate } = values;
      const formValued = { name, coordinate };

      if (currentUserMeasurement === CycleMeasurement.Fahrenheit) {
        formValued.coordinate.y = Number(fahrenheitToCelsius(formValued.coordinate.y));
      }

      if (linkTargetNoteId) {
        const response = await createNoteLink({
          noteId: linkTargetNoteId,
          payload: { coordinate: formValued.coordinate },
        });
        addLinkToListNotes(linkTargetNoteId, response);

        const updatedListNotes = cycleRef.current.notes.map((noteData) =>
          noteData.id === linkTargetNoteId
            ? { ...noteData, links: [...(noteData.links ?? []), response] }
            : noteData
        );
        updateCycleData({ notes: updatedListNotes });

        closeModalCreation();
        return;
      }

      const response = await createNote({ ...formValued, cycleId: id });
      expandListNotes(response);

      updateCycleData({ notes: [...cycleRef.current.notes, response] });

      closeModalCreation();
      return;
    }

    if (isDeletionNote) {
      const { id } = values;

      const response = await deleteNote({ id, deleteAll: isDeletingChainRef.current });

      if (response?.promoted) {
        promoteLinkInListNotes(id, response.promotedLinkId, response.coordinate);

        const updatedListNotes = cycleRef.current.notes.map((noteData) =>
          noteData.id === id
            ? {
                ...noteData,
                coordinate: response.coordinate,
                links: (noteData.links ?? []).filter((link) => link.id !== response.promotedLinkId),
              }
            : noteData
        );
        updateCycleData({ notes: updatedListNotes });

        closeModalEditing();
        return;
      }

      deleteNoteFromListNotes(id);

      const updatedListNotes = cycleRef.current.notes.filter((noteData) => noteData.id !== id);

      updateCycleData({ notes: updatedListNotes });

      closeModalEditing();

      // Confirmed (not just a stale-backend symptom): the incremental chart patch
      // above doesn't reliably remove a whole marker from the live chart — force a
      // full resync, matching what a manual refresh already does reliably.
      // NOTE: `id` here is the note's id (destructured from `values` above),
      // not the cycle's — use the cycle id via the ref to avoid that shadowing.
      await fetchData(cycleRef.current.id, dateRange);
      return;
    }

    if (isEditNote) {
      const { name, id, coordinate } = values;
      const payload = { name };
      const response = await updateNote({
        id: values.id,
        payload,
      });

      const oldNoteData = { name, id, coordinate };
      updateListNotes(oldNoteData, response);

      const existingLinks = cycleRef.current.notes.find((noteData) => noteData.id === id)?.links;
      const updatedListNotes = cycleRef.current.notes.filter((noteData) => noteData.id !== id);
      updatedListNotes.push({ ...response, links: existingLinks });

      updateCycleData({ notes: updatedListNotes });

      closeModalEditing();
      return;
    }
  };

  const noteValidationSchema = useMemo(
    () =>
      object({
        name: linkTargetNoteId
          ? string().notRequired()
          : string()
              .min(TEXT_LENGTH_LIMITS.MIN, `Minimum ${TEXT_LENGTH_LIMITS.MIN} characters required`)
              .max(TEXT_LENGTH_LIMITS.MAX, `Maximum ${TEXT_LENGTH_LIMITS.MAX} characters required`)
              .required('Required'),
      }),
    [linkTargetNoteId],
  );

  const formik = useFormik<NoeFormValues>({
    initialValues: {
      id: null,
      name: '',
      coordinate: { x: null, y: null },
    },
    validationSchema: noteValidationSchema,
    onSubmit: onSubmitFormHandler,
  });

  const isDisabledNoteBtn = Boolean(formik.touched.name && formik.errors.name);

  const hasSufficientDataForRange = (
    iotData: Record<string, IoTDataResponse[]> | undefined,
    from: number,
    to: number,
  ): { sufficient: boolean; missingFrom?: number; missingTo?: number } => {
    if (!iotData) return { sufficient: false, missingFrom: from, missingTo: to };

    let earliestTimestamp = Infinity;

    Object.values(iotData).forEach((deviceData) => {
      if (deviceData.length > 0) {
        earliestTimestamp = Math.min(earliestTimestamp, deviceData[0].sk);
      }
    });

    if (earliestTimestamp <= from) {
      return { sufficient: true };
    }

    return { sufficient: false, missingFrom: from, missingTo: earliestTimestamp };
  };

  const fetchData = async (id: string, dateRange: number) => {
    const currentDate = new Date().getTime();
    //TODO
    //Create select how many last hours should be loaded
    //1\3\6\12
    const from = currentDate - dateRange;
    const to = currentDate;
    const bucketSize = bucket ? parseInt(bucket) : 1;

    const freshIotData = cycle?.iotDataResponse;

    if (freshIotData && Object.keys(freshIotData).length > 0) {
      const dataCheck = hasSufficientDataForRange(freshIotData as Record<string, IoTDataResponse[]>, from, to);

      if (dataCheck.sufficient) {
        setLastFetchedDate(to);
        return;
      }

      setIsLoading(true);
      const cycleData = await getCycle({
        id,
        query: {
          from: String(dataCheck.missingFrom),
          to: String(dataCheck.missingTo),
          bucket: bucketSize,
        },
      });

      setLastFetchedDate(to);

      if (cycleData.name) {
        setCycleName(cycleData.name);
      }

      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const cycleData = await getCycle({
      id,
      query: {
        from: String(from),
        to: String(to),
        bucket: bucketSize,
      },
    });

    setLastFetchedDate(to);

    if (cycleData.name) {
      setCycleName(cycleData.name);
    }

    setIsLoading(false);
  };

  const updateTempRange = (min: number, max: number, chartPreference: ChartPreference) => {
    const convertedMinTemp = getTempByMeasurement(currentUserMeasurement, chartPreference.min_temperature);
    const convertedMaxTemp = getTempByMeasurement(currentUserMeasurement, chartPreference.max_temperature);

    const min_temperature = applyYAxisPadding(min, { min: convertedMinTemp, max: convertedMaxTemp }, 'min');
    const max_temperature = applyYAxisPadding(max, { min: convertedMinTemp, max: convertedMaxTemp }, 'max');

    setTempRange({ min_temperature, max_temperature });
  };

  const getDeviceNames = async (cycleControllers: CycleController[]) => {
    const promiseChannels = cycleControllers.map((item) => {
      return getChannels(item.controllerId);
    });

    const deviceNames = await getDeviceNamesUtil(cycleControllers, promiseChannels);
    setNameDevices(deviceNames);
  };

  const fetchNewData = async (from: number) => {
    const filterTo = new Date().getTime();

    const bucketSize = bucket ? parseInt(bucket) : 1;
    await getCycle({
      id,
      query: { from: String(from), to: String(filterTo), bucket: bucketSize },
    });
    setLastFetchedDate(filterTo);
  };

  const createNoteByClick = async (event: any, chart: any) => {
    const x = event.offsetX;
    const y = event.offsetY;

    const dataCoords = chart.convertFromPixel({ seriesIndex: 0 }, [x, y]);

    const xValue = dataCoords[0];
    const yValue = dataCoords[1];

    if (linkingNoteIdRef.current) {
      const activeLinkingNoteId = linkingNoteIdRef.current;
      const coordinate = { x: xValue, y: yValue };

      if (currentUserMeasurement === CycleMeasurement.Fahrenheit) {
        coordinate.y = Number(fahrenheitToCelsius(coordinate.y));
      }

      const response = await createNoteLink({ noteId: activeLinkingNoteId, payload: { coordinate } });
      addLinkToListNotes(activeLinkingNoteId, response);

      const updatedListNotes = cycleRef.current.notes.map((noteData) =>
        noteData.id === activeLinkingNoteId
          ? { ...noteData, links: [...(noteData.links ?? []), response] }
          : noteData
      );
      updateCycleData({ notes: updatedListNotes });
      return;
    }

    formik.handleChange({
      target: {
        name: 'coordinate',
        value: { x: xValue, y: yValue },
      },
    });

    openModalCreation();
  };

  const expandListNotes = (note: Note) => {
    const formattedTime = new Date(note.coordinate.x);
    const convertedNoteCoordY = getTempByMeasurement(currentUserMeasurement, note.coordinate.y);

    const newNote: NoteSeriesDataType = {
      name: generateNoteName(note.name),
      id: note.id,
      media: { coord: [formattedTime, convertedNoteCoordY] },
    };

    setCycleComponent((oldValue) => {
      const newNoteSeriesData = [...oldValue.noteSeriesData, newNote].sort(
        (a, b) => new Date(a.media.coord[0]).valueOf() - new Date(b.media.coord[0]).valueOf(),
      );

      const newSource = oldValue.source.map((item) => {
        if (item.time === note.coordinate.x) {
          return { ...item, [note.name]: convertedNoteCoordY };
        }

        return item;
      });

      return {
        ...oldValue,
        noteSeriesData: newNoteSeriesData,
        source: newSource,
      };
    });
  };

  const deleteNoteFromListNotes = (id: string) => {
    setCycleComponent((oldValue) => ({
      ...oldValue,
      noteSeriesData: oldValue.noteSeriesData.filter((note) => note.id !== id),
    }));
  };

  const addLinkToListNotes = (noteId: string, link: NoteLink) => {
    const convertedNoteCoordY = getTempByMeasurement(currentUserMeasurement, link.coordinate.y);
    const formattedTime = new Date(link.coordinate.x);

    setCycleComponent((oldValue) => {
      const newNoteSeriesData = oldValue.noteSeriesData.map((note) => {
        if (note.id !== noteId) {
          return note;
        }

        return {
          ...note,
          links: [...(note.links ?? []), { id: link.id, coord: [formattedTime, convertedNoteCoordY] as [Date, number] }],
        };
      });

      return {
        ...oldValue,
        noteSeriesData: newNoteSeriesData,
      };
    });
  };

  const removeLinkFromListNotes = (noteId: string, linkId: string) => {
    setCycleComponent((oldValue) => {
      const newNoteSeriesData = oldValue.noteSeriesData.map((note) => {
        if (note.id !== noteId) {
          return note;
        }

        return {
          ...note,
          links: (note.links ?? []).filter((link) => link.id !== linkId),
        };
      });

      return {
        ...oldValue,
        noteSeriesData: newNoteSeriesData,
      };
    });
  };

  const promoteLinkInListNotes = (noteId: string, promotedLinkId: string, newCoordinate: { x: number; y: number }) => {
    const convertedNoteCoordY = getTempByMeasurement(currentUserMeasurement, newCoordinate.y);
    const formattedTime = new Date(newCoordinate.x);

    setCycleComponent((oldValue) => {
      const newNoteSeriesData = oldValue.noteSeriesData.map((note) => {
        if (note.id !== noteId) {
          return note;
        }

        return {
          ...note,
          media: { coord: [formattedTime, convertedNoteCoordY] },
          links: (note.links ?? []).filter((link) => link.id !== promotedLinkId),
        };
      });

      return {
        ...oldValue,
        noteSeriesData: sortNoteSeries(newNoteSeriesData),
      };
    });
  };

  const updateListNotes = (oldNote: Note, updatedNote: Note) => {
    const convertedNoteCoordY = getTempByMeasurement(currentUserMeasurement, updatedNote.coordinate.y);
    const formattedTime = new Date(updatedNote.coordinate.x);

    setCycleComponent((oldValue) => {
      const newNoteSeriesData = oldValue.noteSeriesData.map((item) => {
        if (item.id === updatedNote.id) {
          return {
            name: generateNoteName(updatedNote.name),
            id: updatedNote.id,
            media: { coord: [formattedTime, convertedNoteCoordY] },
            links: item.links,
          };
        }

        return item;
      });

      const newSource = oldValue.source.map((item) => {
        if (item.time === formattedTime.valueOf()) {
          const newSourceItem = { ...item };
          delete newSourceItem[oldNote.name];

          newSourceItem[updatedNote.name] = convertedNoteCoordY;

          return newSourceItem;
        }

        return item;
      });

      return {
        ...oldValue,
        noteSeriesData: sortNoteSeries(newNoteSeriesData),
        source: newSource,
      };
    });
  };

  const openModalEditing = (data: Note, occurrenceId: string = 'root') => {
    formik.setValues({
      ...data,
    });

    setEditingOccurrenceId(occurrenceId);
    setIsEditNote(true);
  };

  const closeModalEditing = () => {
    formik.resetForm();
    setIsEditNote(false);
    setIsDeletionNote(false);
    setEditingOccurrenceId('root');
    setIsDeletingChain(false);
  };

  const openModalCreation = () => {
    setIsCreationNote(true);
  };

  const closeModalCreation = () => {
    formik.resetForm();
    setIsCreationNote(false);
    setLinkTargetNoteId(null);
  };

  const showDeleteNote = () => {
    setIsDeletionNote(true);
    setIsDeletingChain(false);
  };

  const showDeleteChain = () => {
    setIsDeletionNote(true);
    setIsDeletingChain(true);
  };

  const closeDeleteNote = () => {
    setIsDeletionNote(false);
    setIsDeletingChain(false);
  };

  const deleteLinkOccurrence = async (noteId: string, linkId: string) => {
    await deleteNoteLink({ noteId, linkId });

    removeLinkFromListNotes(noteId, linkId);

    const updatedListNotes = cycleRef.current.notes.map((noteData) =>
      noteData.id === noteId
        ? { ...noteData, links: (noteData.links ?? []).filter((link) => link.id !== linkId) }
        : noteData
    );
    updateCycleData({ notes: updatedListNotes });

    closeModalEditing();
  };

  const editNoteByClick = (params) => {
    params.event.event.stopPropagation();

    if (linkingNoteIdRef.current) {
      return;
    }

    if (params.componentType === 'markPoint' && formik.values.id === null) {
      setCycleComponent((oldValue) => {
        const { noteSeriesData } = oldValue;
        const note = noteSeriesData.find((note) => params.seriesId.includes(note.id));

        if (!note) {
          return oldValue;
        }

        const { links: _links, ...noteRest } = note;
        const formNote: Note = { ...noteRest, coordinate: { x: note.media.coord[0].valueOf(), y: note.media.coord[1] } };
        const occurrenceId: string = params.data?.occurrenceId ?? 'root';
        openModalEditing(formNote, occurrenceId);

        return oldValue;
      });
    }
  };

  const linkNoteOptions: Option[] = useMemo(() => {
    if (!cycleComponents) return [];

    return cycleComponents.noteSeriesData.map((note, index) => ({
      id: note.id,
      value: `#${index + 1} ${note.name}`,
    }));
  }, [cycleComponents]);

  const selectedLinkOption: Option | null = useMemo(() => {
    if (!linkTargetNoteId) return null;

    return linkNoteOptions.find((option) => option.id === linkTargetNoteId) ?? null;
  }, [linkTargetNoteId, linkNoteOptions]);

  const linkTargetNote = useMemo(() => {
    return cycle?.notes?.find((note) => note.id === linkTargetNoteId) ?? null;
  }, [cycle, linkTargetNoteId]);

  const editingNoteHasLinks = useMemo(() => {
    return (cycleComponents?.noteSeriesData?.find((note) => note.id === formik.values.id)?.links?.length ?? 0) > 0;
  }, [cycleComponents, formik.values.id]);

  const selectLinkTarget = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value as unknown as SelectedValue;
    setLinkTargetNoteId(selectedValue.id === 'DEFAULT_OPTION' ? null : selectedValue.id);
  };

  const startLinkingMode = (noteId: string) => {
    setLinkingNoteId(noteId);
    closeModalEditing();
  };

  const stopLinkingMode = () => {
    setLinkingNoteId(null);
  };

  const changeViewNotes = () => {
    updateViewNotes({
      id: currentUserMember.id,
      is_show_notes: !currentUserMember.is_show_notes,
    });
  };

  useEffect(() => {
    if (id && dateRange) {
      fetchData(id, dateRange);
    }
  }, [id, dateRange]);

  useEffect(() => {
    if (hourRange) {
      const dateRange = TIME.HOUR * hourRange;
      setDateRange(dateRange);
    }
  }, [hourRange]);

  useEffect(() => {
    if (isStopPullingIotData || !lastFetchedDate) return;
    const timeId = setTimeout(() => {
      fetchNewData(lastFetchedDate);
    }, INTERVAL_UPDATE_DATA);

    return () => {
      clearTimeout(timeId);
    };
  }, [isStopPullingIotData, lastFetchedDate]);

  useEffect(() => {
    if (cycleNotes && cycleIotData && currentUserMeasurement) {
      const cycleComponentData = generateCycleData(
        cycleNotes,
        cycleIotData as Record<string, IoTDataResponse[]>,
        currentUserMeasurement,
      );

      const { minTemp, maxTemp } = cycleComponentData;
      setIotData(cycleIotData);
      setCycleComponent(cycleComponentData);
      setMinTemp(minTemp);
      setMaxTemp(maxTemp);
    }
  }, [cycleNotes, cycleIotData, currentUserMeasurement]);

  useEffect(() => {
    if (cycleControllers) {
      const newCycleControllersName = generateControllersName(cycleControllers);

      getDeviceNames(cycleControllers);

      setCycleControllersName(newCycleControllersName);
    }
  }, [cycleControllers]);

  useEffect(() => {
    if (minTemp !== null && maxTemp !== null && chartPreference) {
      updateTempRange(minTemp, maxTemp, chartPreference);
    }
  }, [minTemp, maxTemp, chartPreference]);

  return (
    <>
      <div className="h-full w-full c-live-data">
        {(isLoading || iotData === null) && (
          <div className="c-live-data__spinner flex h-full flex-auto items-center justify-center">
            <SpinnerLoader />
          </div>
        )}
        {!isLoading && iotData && (
          <>
            <div className="c-live-data__info grid items-center pb-6 grid-cols-[auto_auto_1fr_auto] gap-x-4">
              <label className="c-live-data__note-tumbler flex h-full flex-row items-center rounded-md border border-primary px-2 py-1.5">
                <input
                  type="checkbox"
                  className="c-live-data__note-tumbler-checkbox h-5 w-5 flex-auto"
                  onChange={changeViewNotes}
                  checked={currentUserMember.is_show_notes}
                />
                <span className="overflow-hidden text-ellipsis pl-2 text-sm">{t('note.commonText.showNoteText')}</span>
              </label>
              <ReportChannelsWrapper
                cycle={cycle}
                cycleControllers={cycleControllers}
                chartRef={chartRef}
                cycleId={id}
              />
              <div></div>
              <ChartPreferencesWrapper cycleId={id} measurement={currentUserMeasurement} tempRange={tempRange} />
            </div>

            {linkingNoteId && cycleComponents && (
              <div className="mb-4 flex items-center justify-between rounded-md border border-primary bg-primary/10 px-4 py-2">
                <span className="text-sm">
                  {t('note.commonText.addingLinkedNotesFor')} #{getNumberNoteSeries(linkingNoteId, cycleComponents.noteSeriesData)}
                </span>
                <InteractiveButton
                  onClick={stopLinkingMode}
                  text={t('note.commonText.stopAdding')}
                  size={ElementSize.XS}
                  type={ButtonType.PrimaryInverse}
                />
              </div>
            )}

            <TimeRange onChange={(val) => setHourRange(val)} activeRange={hourRange} />

            {!!chartPreference && (
              <div className="h-[calc(100%_-_234px)] w-full sm:h-[calc(100%_-_114px)]">
                <LiveChart
                  cycleControllersName={cycleControllersName}
                  createNote={createNoteByClick}
                  editNote={editNoteByClick}
                  measurement={currentUserMeasurement}
                  cycleComponents={cycleComponents}
                  isNoteView={currentUserMember.is_show_notes}
                  chartPreference={chartPreference}
                  nameDevices={nameDevices}
                  chartRef={chartRef}
                  excludedChannelNames={excludedChannelsNames}
                  cycleControllers={cycleControllers}
                  colorsDevices={colorDevices}
                  generateNoteName={generateNoteName}
                  dateToTimestamp={lastFetchedDate}
                  dateRange={dateRange}
                />
              </div>
            )}
            {IS_ACCESS.NOTE.CREATE && (
              <Modal
                modalTitle="Add note"
                isVisible={isCreationNote}
                onClose={closeModalCreation}
                showAcceptBtn={false}
                showCancelBtn={false}
              >
                <form className="c-live-data__modal flex h-full flex-col" onSubmit={formik.handleSubmit}>
                  {linkNoteOptions.length > 0 && (
                    <div className="pb-4">
                      <SelectOption
                        name="linkTargetNoteId"
                        title={t('note.commonText.linkToExistingNote')}
                        placeholder={t('note.commonText.linkToExistingNotePlaceholder')}
                        options={linkNoteOptions}
                        selectedOption={selectedLinkOption}
                        onChange={selectLinkTarget}
                      />
                    </div>
                  )}
                  {linkTargetNoteId ? (
                    <div className="c-live-data__modal-linked-note-preview">
                      <p className="pb-1.5 text-sm font-medium 2xl:pb-2.5 2xl:text-base">
                        {t('note.commonText.linkedNoteText')}
                      </p>
                      <p className="text-sm 2xl:text-base">{linkTargetNote?.name}</p>
                    </div>
                  ) : (
                    <TextAreaControl
                      label={t('note.commonText.noteLabel')}
                      name="name"
                      placeholder={t('note.commonText.enterNote')}
                      value={formik.values.name}
                      customOnChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      showError={formik.touched.name && formik.errors.name}
                      errorText={formik.errors.name}
                      className="h-full resize-y max-sm:min-h-full max-sm:resize-none sm:max-h-[200px] lg:max-h-[430px]"
                    />
                  )}
                  <div className="c-live-data__modal-actions flex w-full flex-row justify-between pt-4">
                    <InteractiveButton
                      onClick={closeModalCreation}
                      text={t('buttons.cancel')}
                      size={ElementSize.XL}
                      type={ButtonType.PrimaryInverse}
                    />
                    <InteractiveButton
                      text={t('buttons.save')}
                      size={ElementSize.XL}
                      disabled={isDisabledNoteBtn}
                      submit
                    />
                  </div>
                </form>
              </Modal>
            )}
            <Modal
              modalTitle={`${t('note.commonText.editNote')} #${getNumberNoteSeries(
                formik.values.id,
                cycleComponents.noteSeriesData,
              )}`}
              isVisible={isEditNote}
              onClose={closeModalEditing}
              showAcceptBtn={false}
              showCancelBtn={false}
            >
              <form className="c-live-data__modal flex h-full flex-col" onSubmit={formik.handleSubmit}>
                {!isDeletionNote && (
                  <TextAreaControl
                    label={t('note.commonText.noteLabel')}
                    name="name"
                    placeholder={t('note.commonText.enterNote')}
                    value={formik.values.name}
                    customOnChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    showError={formik.touched.name && formik.errors.name}
                    errorText={formik.errors.name}
                    disabled={!IS_ACCESS.NOTE.DELETE || !IS_ACCESS.NOTE.UPDATE}
                    className="h-full resize-y max-sm:min-h-full max-sm:resize-none sm:max-h-[200px] lg:max-h-[430px]"
                  />
                )}
                {isDeletionNote && IS_ACCESS.NOTE.DELETE && (
                  <>
                    <div className="relative flex min-h-[152px] items-center justify-center">
                      <p className="pb-2 text-center text-xl">
                        {isDeletingChain ? t('note.commonText.confirmDeleteNoteChain') : t('note.commonText.confirmDeleteNote')}
                      </p>
                    </div>
                    <div className="c-live-data__modal-actions flex w-full flex-row justify-between pt-4">
                      <InteractiveButton
                        onClick={closeDeleteNote}
                        text={t('buttons.cancel')}
                        size={ElementSize.XL}
                        type={ButtonType.PrimaryInverse}
                      />
                      <InteractiveButton text={t('buttons.delete')} size={ElementSize.XL} submit />
                    </div>
                  </>
                )}
                {!isDeletionNote && IS_ACCESS.NOTE.CREATE && (
                  <div className="c-live-data__modal-actions flex w-full flex-row justify-between pt-4">
                    <InteractiveButton
                      onClick={() => startLinkingMode(formik.values.id)}
                      text={t('note.commonText.addLinkedNotes')}
                      size={ElementSize.XL}
                      type={ButtonType.PrimaryInverse}
                    />
                  </div>
                )}
                {!isDeletionNote && editingNoteHasLinks && IS_ACCESS.NOTE.DELETE && (
                  <div className="c-live-data__modal-actions flex w-full flex-row justify-between pt-4">
                    <InteractiveButton
                      onClick={showDeleteChain}
                      text={t('note.commonText.deleteAllInChain')}
                      size={ElementSize.XL}
                      type={ButtonType.PrimaryInverse}
                    />
                  </div>
                )}
                {!isDeletionNote && IS_ACCESS.NOTE.UPDATE && (
                  <div className="c-live-data__modal-actions flex w-full flex-row justify-between pt-4">
                    <InteractiveButton
                      onClick={editingOccurrenceId === 'root' ? showDeleteNote : () => deleteLinkOccurrence(formik.values.id, editingOccurrenceId)}
                      text={t('buttons.delete')}
                      size={ElementSize.XL}
                      type={ButtonType.PrimaryInverse}
                    />
                    <InteractiveButton
                      text={t('buttons.save')}
                      size={ElementSize.XL}
                      disabled={isDisabledNoteBtn}
                      submit
                    />
                  </div>
                )}
              </form>
            </Modal>
          </>
        )}
      </div>
    </>
  );
};
